import React from "react";
import { useForm } from "react-hook-form";
import TextInput from "components/shared/form/fields/TextInput";
import FormActions from "components/shared/form/fields/FormActions";
import {
  onValidationError,
  resetValidationErrors,
} from "helpers/formValidations";
import { useSaveBlog } from "../api";
import { queryClient } from "helpers/queryClient";
import { Blog } from "components/blogs/types";
import RadioButton from "components/shared/form/fields/RadioButton";
import { useNavigate } from "helpers/tixxt-router";
import PermissionPickerField from "components/shared/form/fields/PermissionPickerField";
import { PermissionPickableWithDisabled } from "components/shared/DirectoryPicker/types";

type BlogFormData = {
  title: string;
  description: string;
  publish_as: string;
  permitted_audience_for_create_and_update: PermissionPickableWithDisabled[];
  permitted_audience_for_publish_and_unpublish: PermissionPickableWithDisabled[];
};

export default function BlogForm({ blog }: { blog: Blog }) {
  const navigate = useNavigate();
  const { register, control, handleSubmit } = useForm<BlogFormData>({
    defaultValues: {
      title: blog.title || "",
      description: blog.description || "",
      publish_as: blog.publish_as || "",
      permitted_audience_for_create_and_update:
        blog?.policy_settings?.create_and_update?.permitted_audience || [],
      permitted_audience_for_publish_and_unpublish:
        blog?.policy_settings?.publish_and_unpublish.permitted_audience || [],
    },
  });

  const { mutate: saveBlog } = useSaveBlog(blog, {
    onSuccess: (responseData) => {
      queryClient.setQueryData(`/blogs/${responseData.id}`, responseData);
      navigate(`/blogs/${responseData.id}`);
      toastr.success(I18n.t("js.saving_successful"));
    },
    onError: (error: { message: string }) => {
      toastr.error(error.message || I18n.t("js.saving_failure"));
    },
  });

  function submit({
    title,
    description,
    publish_as,
    permitted_audience_for_create_and_update,
    permitted_audience_for_publish_and_unpublish,
  }: BlogFormData) {
    const blogData = {
      blog: {
        id: blog.id,
        parent_id: blog.parent_id,
        title: title,
        description: description,
        publish_as: publish_as,
        policy_settings: {
          create_and_update: {
            permitted_audience: permitted_audience_for_create_and_update,
          },
          publish_and_unpublish: {
            permitted_audience: permitted_audience_for_publish_and_unpublish,
          },
        },
      },
    };

    resetValidationErrors();
    saveBlog(blogData);
  }

  return (
    <form
      className="form-horizontal mb-6"
      onSubmit={handleSubmit(submit, onValidationError)}
    >
      <TextInput
        name="title"
        label={I18n.t("js.blog.form.title")}
        register={register}
        required={I18n.t("js.blog.form.title_required")}
      />
      <TextInput
        name="description"
        label={I18n.t("js.blog.form.description")}
        register={register}
        multiline
      />
      <RadioButton
        name="publish_as"
        label={I18n.t("js.blog.form.publish_as.label")}
        register={register}
        defaultValue={blog.publish_as || "blog_title"}
        options={[
          {
            label: I18n.t("js.blog.form.publish_as.blog_title"),
            value: "blog_title",
          },
          { label: I18n.t("js.blog.form.publish_as.author"), value: "author" },
        ]}
        hint={I18n.t("js.blog.form.publish_as.hint")}
      />

      <PermissionPickerField
        name="permitted_audience_for_create_and_update"
        label={I18n.t("js.blog.form.create_and_update.label")}
        hint={I18n.t("js.blog.form.create_and_update.hint")}
        control={control}
      />

      <PermissionPickerField
        name="permitted_audience_for_publish_and_unpublish"
        label={I18n.t("js.blog.form.publish_and_unpublish.label")}
        hint={I18n.t("js.blog.form.publish_and_unpublish.hint")}
        control={control}
      />
      <FormActions
        onCancel={(e) => {
          e.preventDefault();
          navigate(`/blogs/${blog.id}`);
        }}
      />
    </form>
  );
}
